import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Input } from 'reactstrap';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';

import * as actions from './actions';
import { Wrapper, Container, Button, Form } from '../../components';
import { Header, HeadingContainer } from '../ChallengeListPage/styles';
import { InputAndSearchSection, InputSection, CreditsTag } from './styles';
import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableHeading,
  TableData,
  SortArrowImage,
} from '../../components/cmsRankList/style';
import Pagination from '../../components/Pagination';
import { userRoles } from 'util/appConstants';
import { useActions } from 'hooks/useActions';
import { Images } from 'util/images';
import * as profileActions from '../../containers/ProfilePage/actions';
import Path from 'routes/Path';

export default React.memo(() => {
  const [transactions, setTransactions] = useState([]);
  const [paginatedItems, setPaginatedItems] = useState([]);
  const [sortBy, setSortBy] = useState('name');
  const [isAscending, setIsAscending] = useState(false);
  const [comment, setComment] = useState('');
  const [credits, setCredits] = useState(100);
  const history = useHistory();

  const { userRole } = useSelector((state) => state.auth);
  const isSuperAdminOrReseller = userRole === userRoles.SUPER_ADMIN || userRole === userRoles.RESELLER ? true : false;
  const itemsPerPage = 10;
  const { getPayments, createPayment } = useActions(actions);
  const { getProfile } = useActions(profileActions);

  let { loading, payments, transaction, error } = useSelector((state) => state.payments);

  useEffect(() => {
    getPayments();
  }, [getPayments]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  useEffect(() => {
    if (transaction.payment) {
      const newList = [transaction.payment, ...transactions];
      setTransactions(newList);
      if (transactions.length <= itemsPerPage) setPaginatedItems(newList);
      setComment('');
      setCredits(100);
      getProfile();
      toast.success('Transaction successful');
    }
  }, [transaction]);

  useEffect(() => {
    setTransactions(payments);
    if (transactions.length <= itemsPerPage) setPaginatedItems(payments);
  }, [payments]);

  const sort = (array, field, isAscending) => {
    let sortableItems = [...array];
    return sortableItems.sort((a, b) => {
      let x = field === 'accountName' ? a[field].toLowerCase() : a[field];
      let y = field === 'accountName' ? b[field].toLowerCase() : b[field];
      if (x < y) {
        return isAscending ? -1 : 1;
      }
      if (x > y) {
        return isAscending ? 1 : -1;
      }
      return 0;
    });
  };

  const sortListBy = (field) => {
    let updatedList = sort(transactions, field, field !== sortBy ? false : isAscending);
    setTransactions(updatedList);
    if (field !== sortBy) {
      setIsAscending(true);
    } else {
      setIsAscending(!isAscending);
    }
    setSortBy(field);
  };

  const createManualPayment = () => {
    if (!Number(credits) || Number(credits) % 100 !== 0) return toast.error('Credits should be multiple of 100');
    let body = {
      credits: Number(credits),
      comment: comment,
      isManualPayment: true,
    };
    createPayment(body);
  };

  const renderTopSection = () => {
    return (
      <Form onSubmit={() => createManualPayment()}>
        <InputAndSearchSection>
          <InputSection>
            <Input
              type="text"
              placeholder="Comment (optional)"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </InputSection>
          <InputSection>
            <Input type="number" placeholder="Credits" value={credits} onChange={(e) => setCredits(e.target.value)} />
          </InputSection>
          <InputSection>
            <Button type="submit" padding="5px 15px" margin="0" primaryBlue primaryGreenBorder>
              Create Payment
            </Button>
          </InputSection>
        </InputAndSearchSection>
      </Form>
    );
  };

  const renderTableHeading = () => {
    return (
      <TableRow isHeading>
        <TableHeading>
          Account
          <SortArrowImage
            onClick={() => sortListBy('accountName')}
            alt="arrow"
            src={sortBy === 'accountName' && isAscending ? Images.upArrow : Images.downArrow}
          />
        </TableHeading>
        <TableHeading hideOnSmallDevice>Comment</TableHeading>
        <TableHeading hideOnSmallDevice>Active Plan</TableHeading>
        <TableHeading>
          Date
          <SortArrowImage
            onClick={() => sortListBy('createdAt')}
            alt="arrow"
            src={sortBy === 'createdAt' && isAscending ? Images.upArrow : Images.downArrow}
          />
        </TableHeading>
        <TableHeading>
          Tests
          <SortArrowImage
            onClick={() => sortListBy('credits')}
            alt="arrow"
            src={sortBy === 'credits' && isAscending ? Images.upArrow : Images.downArrow}
          />
        </TableHeading>
        <TableHeading>Total Tests</TableHeading>
        <TableHeading>Receipt</TableHeading>
      </TableRow>
    );
  };

  const renderTableBody = () => {
    return paginatedItems.map((item, index) => {
      return (
        <TableRow key={index}>
          <TableData>{item.accountName}</TableData>
          <TableData hideOnSmallDevice>{item.comment}</TableData>
          <TableData hideOnSmallDevice>{item.plan || ''}</TableData>
          <TableData>{moment(item.createdAt).format('YYYY-MM-DD')}</TableData>
          <TableData>
            <CreditsTag isPositive={item.credits / 100 > 0}>
              {item.credits / 100} {Math.abs(item.credits) / 100 === 1 ? 'test' : 'tests'}
            </CreditsTag>
          </TableData>
          <TableData>
            {item.newTotal && Math.floor(item.newTotal / 100)}{' '}
            {item.newTotal >= 0 ? (Math.floor(item.newTotal / 100) > 1 ? 'tests' : 'test') : ''}
            {item.payStatus && item.payStatus !== 'succeeded' && item.payStatus && item.payStatus !== 'trial' && (
              <CreditsTag isPositive={false}>Failed</CreditsTag>
            )}
          </TableData>
          <TableData>
            {item.receipt_url ? (
              <a href={item.receipt_url} target="_blank" rel="noopener noreferrer">
                <i className="fa fa-external-link"></i>
              </a>
            ) : null}
          </TableData>
        </TableRow>
      );
    });
  };

  return (
    <Wrapper padding="60px 0 0 0" minHeight="100vh">
      <Container column>
        <HeadingContainer>
          <Header>Payments</Header>
          {!isSuperAdminOrReseller && (
            <Button
              padding="5px 15px"
              margin="10px 0 0 0"
              onClick={() => history.push(Path.subscriptionPlans)}
              primaryBlue
              primaryGreenBorder
            >
              Purchase Tests
            </Button>
          )}
        </HeadingContainer>
        {isSuperAdminOrReseller ? renderTopSection() : null}
        {transactions && transactions.length && !loading ? (
          <>
            <Table margin={isSuperAdminOrReseller ? '' : '30px 0 0 0'}>
              <TableHead>{renderTableHeading()}</TableHead>
              <TableBody>{renderTableBody()}</TableBody>
            </Table>
            {transactions.length > itemsPerPage && (
              <Pagination items={transactions} itemsPerPage={itemsPerPage} getItems={setPaginatedItems} />
            )}
          </>
        ) : loading ? (
          'Loading'
        ) : (
          'No data found'
        )}
      </Container>
    </Wrapper>
  );
});
